/**
 * UIEffectsContext
 *
 * Hoists site-wide UI effect state (currently: glitch transition active flag)
 * above the page level so that any component in the tree can trigger the
 * glitch overlay and the overlay portal can render once near the root.
 *
 * Phase 31: dormant scaffolding — no consumers yet. The provider is mounted
 *           inside <Router> in App.tsx so Phase 35's GlitchTransition can
 *           call useNavigate() from inside the provider's children.
 * Phase 35: SECRET-01 mounts useSecretSequence on the homepage and calls
 *           triggerGlitch(); SECRET-02's <GlitchTransition /> reads
 *           glitchActive and calls completeGlitch() on animationend.
 *
 * Exports:
 *   - UIEffectsContextValue: TypeScript interface (named export so consumers
 *                            can type their own helpers if needed)
 *   - UIEffectsProvider: React component, accepts children, owns glitchActive
 *                        state internally with useState(false)
 *   - useUIEffects: consumer hook that throws a clear Error when called
 *                   outside the provider (Phase 35 debug aid per Pitfall 6)
 */

import React, { createContext, useContext, useState } from 'react';

export interface UIEffectsContextValue {
  glitchActive: boolean;
  triggerGlitch: () => void;
  completeGlitch: () => void;
}

const UIEffectsContext = createContext<UIEffectsContextValue | null>(null);

export const UIEffectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [glitchActive, setGlitchActive] = useState<boolean>(false);

  const triggerGlitch = (): void => {
    setGlitchActive(true);
  };

  const completeGlitch = (): void => {
    setGlitchActive(false);
  };

  const value: UIEffectsContextValue = {
    glitchActive,
    triggerGlitch,
    completeGlitch,
  };

  return (
    <UIEffectsContext.Provider value={value}>
      {children}
    </UIEffectsContext.Provider>
  );
};

export const useUIEffects = (): UIEffectsContextValue => {
  const ctx = useContext(UIEffectsContext);
  if (!ctx) {
    throw new Error('useUIEffects must be called inside UIEffectsProvider');
  }
  return ctx;
};
